import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, Inbox, RefreshCw } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { MatchedClient } from "@/types/staffing/matchedClient";
import OpeningCard, { OpeningsBlockerAlert } from "./openings/openingsUi";
import { useOpenings } from "./hooks/useOpenings";

const OpeningsSkeleton = () => (
    <div className="flex flex-col gap-3">
        {[0, 1, 2].map(i => (
            <Card key={i}>
                <CardContent className="flex flex-col gap-3 p-4">
                    <div className="flex items-center gap-3">
                        <Skeleton className="h-10 w-10 rounded-full" />
                        <div className="flex-1 space-y-2">
                            <Skeleton className="h-4 w-32" />
                            <Skeleton className="h-3 w-24" />
                        </div>
                    </div>
                    <Skeleton className="h-2 w-full" />
                    <div className="grid grid-cols-2 gap-3">
                        <Skeleton className="h-8" />
                        <Skeleton className="h-8" />
                    </div>
                </CardContent>
            </Card>
        ))}
    </div>
);

/**
 * Mobile view of the Openings widget: the same matched clients as
 * ClientMatchesList, rendered as a list of cards rather than a table.
 * Tapping a card opens the single opening view.
 */
const Openings = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { openings, loading, error, blocker, refresh } = useOpenings();

    const openDetail = (opening: MatchedClient) => {
        // The detail view fetches by posting, so a potential match without an
        // active posting has no detail page to open
        if (opening.postingID == null) return;
        // Carry host supplied params (darkTheme, ...) through to the detail view
        const params = new URLSearchParams(searchParams);
        params.set('postingId', String(opening.postingID));
        navigate(`/embedded-app/openings/detail?${params.toString()}`);
    };

    return (
        <div className="mx-auto flex w-full max-w-xl flex-col gap-3 p-3 pb-8">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-lg font-semibold">Openings</h1>
                    <p className="text-xs text-muted-foreground">
                        {loading ? 'Loading your matches...'
                            : blocker ? 'Matches unavailable'
                                : `${openings.length} match${openings.length !== 1 ? 'es' : ''} near you`}
                    </p>
                </div>
                <Button variant="ghost" size="sm" onClick={refresh} disabled={loading} aria-label="Refresh openings">
                    <RefreshCw className={loading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
                </Button>
            </div>

            {error && (
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Couldn't load openings</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            )}

            <OpeningsBlockerAlert blocker={blocker} />

            {loading && <OpeningsSkeleton />}

            {!loading && !error && !blocker && openings.length === 0 && (
                <div className="flex flex-col items-center gap-2 py-12 text-center text-muted-foreground">
                    <Inbox className="h-8 w-8" />
                    <p className="text-sm">No openings match your profile right now.</p>
                </div>
            )}

            {!loading && openings.map(opening => (
                <OpeningCard key={opening.clientID} opening={opening} onSelect={openDetail} />
            ))}
        </div>
    );
};

export default Openings;
